// mailListRecognition.ts — Cluster POSTFACH (G1/G3)
//
// Die EINE Stelle, an der aus einer Mail der LISTE (also ohne geöffnetes
// Detailfenster und ohne geladenen Verlauf) der zugewiesene Kontakt ermittelt
// wird.
//
// Aus dem Kontextmenü heraus gibt es kein Fenster, das die Erkennung schon
// geladen hätte — und die Rechtsklick-Wege sollen NICHT zu einem anderen
// Ergebnis kommen als der Weg über die geöffnete Mail. Deshalb läuft hier
// dieselbe Quelle wie im Detailfenster: der serverseitige Erkennungs-Endpunkt
// aus Cluster ERKENNUNG (A1), `GET /mail/recognize`. Er sucht die Kandidaten
// über die ganze Konversation (Von/An/Cc aller Nachrichten, interne Adressen
// nie) — der frühere Frontend-Fallback über den geladenen Verlauf ist mit
// Cluster ERKENNUNG entfallen und wird hier bewusst NICHT wiederbelebt.
//
// Fehlt die Server-Antwort (Fehler, altes Backend), gilt als Notnagel die
// Listen-Annotation der Mail — genau wie im Detailfenster, wo `pickDisplayContact`
// denselben Rückfall macht. Das ist ausdrücklich als `degraded` markiert, damit
// die Oberfläche es benennen kann, statt „nichts erkannt" vorzutäuschen.

import {
    graphMailService,
    type Email,
    type MailSenderCandidate,
} from "../../services/graphMailService";
import {
    defaultCandidateSelection,
    effectiveSenderCandidates,
    resolveCandidateAssignment,
    sortSenderCandidates,
} from "./mailCommandState";

export type MailRecognition = {
    /** Erkennungs-Kandidaten in Anzeige-Reihenfolge (Absender → An → Cc, Firma → AP → Person). */
    candidates: MailSenderCandidate[];
    /** Vorauswahl unter den Kandidaten — `null`, wenn nichts stark genug ist. */
    defaultContactId: number | null;
    /** Der Kontakt, mit dem Vorgang/Picker vorbelegt werden. `null` = nicht erkannt. */
    assignment: { id: number; displayName: string } | null;
    /**
     * true = die serverseitige Erkennung war nicht verfügbar und es gilt nur die
     * Listen-Annotation. NICHT dasselbe wie „nichts erkannt".
     */
    degraded: boolean;
};

/**
 * Erkennung aus einer fertigen Kandidatenliste ableiten — ohne Netzaufruf.
 *
 * Ein Ansprechpartner wird dabei auf seine Firma umgelegt
 * (`resolveCandidateAssignment`), genau wie in der Command-Leiste der geöffneten
 * Mail: der Vorgang gehört der Firma, die AP-Beziehung bleibt nur eine Info.
 */
export function recognitionFromCandidates(candidates: MailSenderCandidate[]): MailRecognition {
    const sorted = sortSenderCandidates(candidates);
    const defaultContactId = defaultCandidateSelection(sorted);
    const gewaehlt = sorted.find((c) => c.contactId === defaultContactId) ?? null;
    return {
        candidates: sorted,
        defaultContactId,
        assignment: gewaehlt ? resolveCandidateAssignment(gewaehlt, sorted) : null,
        degraded: false,
    };
}

/**
 * Erkennung für eine Listen-Mail. Ein Netzaufruf (`GET /mail/recognize`), danach
 * dieselben Werte, die auch das Detailfenster benutzt.
 *
 * `assignment` ist im Erfolgsfall der SERVERSEITIG bestimmte Kontakt
 * (`contactRecognition.contact`) — dieselbe Quelle wie `displayContact` im
 * Detailfenster, und damit auch dieselbe Regel „bei einem Ansprechpartner
 * gewinnt die Firma" (der Server rangiert Firma vor AP vor Person). Auf die
 * Kandidaten-Vorauswahl wird nur zurückgefallen, wenn es gar keine
 * Server-Antwort gibt.
 */
export async function resolveMailRecognition(
    mailbox: string,
    email: Email,
): Promise<MailRecognition> {
    const outcome = await graphMailService.recognizeMail(mailbox, email.id);
    const server = outcome.status === "ok" ? outcome.data.contactRecognition ?? null : null;

    if (server) {
        const sorted = sortSenderCandidates(server.candidates);
        const kontakt = server.contact
            ? { id: server.contact.contactId, displayName: server.contact.displayName }
            : null;
        return {
            candidates: sorted,
            defaultContactId: kontakt?.id ?? defaultCandidateSelection(sorted),
            // Ein `contact: null` des Servers ist eine AUSSAGE („nicht erkannt",
            // etwa im reinen Domänen-Fall) und wird nicht durch die
            // Listen-Annotation überstimmt — sonst stünden zwei Wahrheiten
            // nebeneinander (dieselbe Regel wie in `pickDisplayContact`).
            assignment: kontakt,
            degraded: false,
        };
    }

    // Notnagel ohne Server-Antwort: die Listen-Annotation. Zweites Argument ist
    // `recognizedContact` und NICHT `matchedContact` — dort stünde auch ein
    // interner Kollege, den A2 aus der Anzeige heraushält.
    const fallback = recognitionFromCandidates(
        effectiveSenderCandidates(email.senderCandidates, email.recognizedContact),
    );
    return { ...fallback, degraded: outcome.status === "error" };
}

/**
 * Wer trägt den Vorgang, der aus einer Listen-Mail entsteht?
 *
 * Die Erkennung rangiert Firma vor Ansprechpartner (`assignment` ist bei einem
 * AP-Absender die FIRMA). Für den VORGANG gilt seit PO 28.09.2026 (#1829,
 * `vorgangKontaktAus`) aber der Mensch, der geschrieben hat: „für eine Person,
 * die zu einer Firma gehört, steht bei Kontakt die Firma, obwohl ich ja die
 * Person ausgewählt habe". Deshalb hier: ist `assignment` eine Firma und steht
 * unter den Kandidaten ein Ansprechpartner DIESER Firma aus dem Absenderfeld,
 * trägt er den Vorgang. Die Firma bleibt über seine Beziehung erreichbar.
 */
export function vorgangsKontaktAusErkennung(
    erkennung: Pick<MailRecognition, "assignment" | "candidates">,
): { id: number; displayName: string } | null {
    const a = erkennung.assignment;
    if (!a) return null;
    const ap = erkennung.candidates.find(
        (c) =>
            c.kind === "ap" &&
            c.viaCompanyId === a.id &&
            (c.foundIn ?? "from") === "from" &&
            c.matchedBy !== "domain",
    );
    return ap ? { id: ap.contactId, displayName: ap.displayName } : a;
}
